import { FormikErrors } from 'formik';
import { useCallback } from 'react';

import { ValidationResponse } from '../../../../types/ValidationResponse';
import { validateOrderUpdate } from '../../../common/api/clients/order.api';
import { queries } from '../../../common/api/queryKeys/queries';
import { toNullableNumber } from '../../../common/helpers/toNullableNumber.helper';
import { mapValidationResponseToFormikErrors } from '../../../common/validation/mapValidationResponseToFormikErrors';
import { useBackendValidationRequest } from '../../../common/validation/useBackendValidationRequest';
import { FormikEditOrderValues } from '../OrderEditForm';

interface ValidateOrderUpdateRequest {
    orderId: string;
    validationRequest: {
        state: FormikEditOrderValues['state'];
        vesselId: string;
        portId: string;
        dateDelivery: FormikEditOrderValues['dateDelivery'];
        buyerReference: string | null;
        agentId: string | null;
        receiverSupplierIds: string[];
        spot: boolean;
        items: {
            productId: string | null;
            packType: string;
            unitSize: number | null;
            units: number | null;
            unit: string;
            deleted: boolean;
        }[];
        samplekits: FormikEditOrderValues['samplekits'];
    };
}

export const useValidateOrderEditForm = () => {
    const requestValidation = useBackendValidationRequest<ValidateOrderUpdateRequest, ValidationResponse>({
        queryKeyPrefix: queries.backendValidation.orderLubesEdit.queryKey,
        request: ({ orderId, validationRequest }) => validateOrderUpdate(validationRequest, orderId),
    });

    return useCallback(
        async (values: FormikEditOrderValues): Promise<FormikErrors<FormikEditOrderValues>> => {
            const validationRequest: ValidateOrderUpdateRequest['validationRequest'] = {
                state: values.state,
                vesselId: values.vesselId,
                portId: values.portId,
                dateDelivery: values.dateDelivery,
                buyerReference: values.buyerReference || null,
                agentId: values.agentId || null,
                receiverSupplierIds: values.receiverSupplierIds || [],
                spot: values.spot,
                items: values.items.map((item) => ({
                    productId: item.productId || null,
                    packType: item.packType,
                    unitSize: toNullableNumber(item.unitSize),
                    units: toNullableNumber(item.units),
                    unit: item.unit,
                    deleted: item.deleted,
                })),
                samplekits: values.samplekits,
            };
            const validationKey = JSON.stringify({
                orderId: values.id,
                validationRequest,
            });

            try {
                const response = await requestValidation({
                    validationKey,
                    params: {
                        orderId: values.id,
                        validationRequest,
                    },
                });

                return mapValidationResponseToFormikErrors<FormikEditOrderValues>(response);
            } catch {
                return {};
            }
        },
        [requestValidation]
    );
};
